import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../../../services';
import { utils } from '../../../utils';
import FormLayout from '../../../components/Common/FormLayout';

interface OccurrenceFormData {
  titulo: string;
  descricao: string;
  tipo: string;
  gravidade: 'Baixa' | 'Média' | 'Alta';
  status: 'Pendente' | 'Em Andamento' | 'Resolvida';
  data_ocorrencia: string;
  data_resolucao: string;
  veiculo_id: number | '';
  motorista_id: number | '';
  local: string;
  responsavel: string;
  observacoes_resolucao: string;
}

interface Vehicle {
  id: number;
  placa: string;
  marca: string;
  modelo: string;
}

interface Driver {
  id: number;
  nome: string;
}

const OccurrenceCreate: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<OccurrenceFormData>({
    titulo: '',
    descricao: '',
    tipo: 'Acidente',
    gravidade: 'Média',
    status: 'Pendente',
    data_ocorrencia: new Date().toISOString().slice(0, 16), // Data e hora atual no formato correto
    data_resolucao: '',
    veiculo_id: '',
    motorista_id: '',
    local: '',
    responsavel: '',
    observacoes_resolucao: ''
  });
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [errors, setErrors] = useState<Record<string, string | undefined>>({});

  useEffect(() => {
    loadVehiclesAndDrivers();
  }, []);

  const loadVehiclesAndDrivers = async () => {
    try {
      setIsLoadingData(true);

      // Carregar veículos
      const vehiclesResponse = await apiService.getVehicles({});
      if (vehiclesResponse.success && vehiclesResponse.data) {
        const veiculosApi = Array.isArray(vehiclesResponse.data) ? vehiclesResponse.data : [];
        setVehicles(veiculosApi.map((v: any) => ({
          id: v.id,
          placa: v.placa,
          marca: v.marca,
          modelo: v.modelo
        })));
      }

      // Carregar motoristas
      const driversResponse = await apiService.getDrivers({});
      if (driversResponse.success && driversResponse.data) {
        const motoristasApi = Array.isArray(driversResponse.data) ? driversResponse.data : [];
        setDrivers(motoristasApi.map((d: any) => ({
          id: d.id,
          nome: d.nome
        })));
      }
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      utils.showNotification('Erro ao carregar veículos e motoristas', { type: 'error' });
    } finally {
      setIsLoadingData(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Limpar erro do campo quando o usuário começar a digitar
    if (errors[name as keyof OccurrenceFormData]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.titulo.trim()) {
      newErrors.titulo = 'Título é obrigatório';
    }

    if (!formData.descricao.trim()) {
      newErrors.descricao = 'Descrição é obrigatória';
    }

    if (!formData.tipo.trim()) {
      newErrors.tipo = 'Tipo é obrigatório';
    }

    if (!formData.gravidade) {
      newErrors.gravidade = 'Gravidade é obrigatória';
    }

    if (!formData.data_ocorrencia) {
      newErrors.data_ocorrencia = 'Data e hora da ocorrência são obrigatórias';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      utils.showNotification('Por favor, corrija os erros no formulário', { type: 'error' });
      return;
    }

    // Verificar autenticação antes de enviar
    const token = localStorage.getItem('confereai_token');
    if (!token) {
      console.error('❌ [OccurrenceCreate] Token não encontrado ao tentar criar ocorrência');
      console.error('❌ [OccurrenceCreate] Redirecionando para login...');
      utils.showNotification('Sua sessão expirou. Por favor, faça login novamente.', { type: 'error' });
      setTimeout(() => {
        navigate('/login');
      }, 2000);
      return;
    }

    setIsLoading(true);
    try {
      // Preparar dados para envio
      const submitData = {
        titulo: formData.titulo,
        descricao: formData.descricao,
        tipo: formData.tipo,
        gravidade: formData.gravidade,
        status: formData.status,
        data_ocorrencia: formData.data_ocorrencia,
        data_resolucao: formData.data_resolucao || undefined,
        veiculo_id: formData.veiculo_id === '' ? undefined : Number(formData.veiculo_id),
        motorista_id: formData.motorista_id === '' ? undefined : Number(formData.motorista_id),
        local: formData.local || undefined,
        responsavel: formData.responsavel || undefined,
        observacoes_resolucao: formData.observacoes_resolucao || undefined
      };

      const response = await apiService.createOccurrence(submitData);

      if (response.success) {
        utils.showNotification('Ocorrência cadastrada com sucesso!', { type: 'success' });
        navigate('/ocorrencias');
      } else {
        throw new Error(response.message || 'Erro ao cadastrar ocorrência');
      }
    } catch (error: any) {
      console.error('Erro ao cadastrar ocorrência:', error);
      utils.showNotification(error.message || 'Erro ao cadastrar ocorrência', { type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = () => {
    // Criar um evento simulado para chamar handleSubmit
    const fakeEvent = {
      preventDefault: () => {}
    } as React.FormEvent;
    handleSubmit(fakeEvent);
  };

  const handleCancel = () => {
    navigate('/ocorrencias');
  };

  if (isLoadingData) {
    return (
      <FormLayout
        title="Nova Ocorrência"
        subtitle="Registro de Ocorrência"
        onCancel={handleCancel}
        onSave={handleSave}
        saveButtonText="Cadastrar Ocorrência"
        isLoading={true}
        showActions={false}
      >
        <div style={{ textAlign: 'center', padding: '100px 20px' }}>
          <div className="loading-spinner-large"></div>
          <p>Carregando dados...</p>
        </div>
      </FormLayout>
    );
  }

  return (
    <FormLayout
      title="Nova Ocorrência"
      subtitle="Registro de Ocorrência"
      onCancel={handleCancel}
      onSave={handleSave}
      saveButtonText="Cadastrar Ocorrência"
      isLoading={isLoading}
      showActions={false}
    >
      <form onSubmit={handleSubmit}>
        {/* Informações Básicas */}
        <div style={{ marginBottom: '30px' }}>
          <h3 style={{ marginBottom: '20px', color: '#2c3e50', borderBottom: '2px solid #04C64F', paddingBottom: '10px' }}>
            Informações Básicas
          </h3>

          <div className="info-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
            <div className="form-group">
              <label className="form-label">Título *</label>
              <input
                type="text"
                name="titulo"
                value={formData.titulo}
                onChange={handleInputChange}
                className={`form-control ${errors.titulo ? 'error' : ''}`}
                placeholder="Ex: Colisão no pátio"
                maxLength={255}
              />
              {errors.titulo && <span className="error-message">{errors.titulo}</span>}
            </div>

            <div className="form-group">
              <label className="form-label">Tipo *</label>
              <select
                name="tipo"
                value={formData.tipo}
                onChange={handleInputChange}
                className={`form-control ${errors.tipo ? 'error' : ''}`}
              >
                <option value="Acidente">Acidente</option>
                <option value="Avaria">Avaria</option>
                <option value="Furto">Furto</option>
                <option value="Roubo">Roubo</option>
                <option value="Mau Uso">Mau Uso</option>
                <option value="Quebra">Quebra</option>
                <option value="Multa">Multa</option>
                <option value="Outro">Outro</option>
              </select>
              {errors.tipo && <span className="error-message">{errors.tipo}</span>}
            </div>

            <div className="form-group">
              <label className="form-label">Gravidade *</label>
              <select
                name="gravidade"
                value={formData.gravidade}
                onChange={handleInputChange}
                className={`form-control ${errors.gravidade ? 'error' : ''}`}
              >
                <option value="Baixa">Baixa</option>
                <option value="Média">Média</option>
                <option value="Alta">Alta</option>
              </select>
              {errors.gravidade && <span className="error-message">{errors.gravidade}</span>}
            </div>

            <div className="form-group">
              <label className="form-label">Status *</label>
              <select
                name="status"
                value={formData.status}
                onChange={handleInputChange}
                className="form-control"
              >
                <option value="Pendente">Pendente</option>
                <option value="Em Andamento">Em Andamento</option>
                <option value="Resolvida">Resolvida</option>
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Data e Hora da Ocorrência *</label>
              <input
                type="datetime-local"
                name="data_ocorrencia"
                value={formData.data_ocorrencia}
                onChange={handleInputChange}
                className={`form-control ${errors.data_ocorrencia ? 'error' : ''}`}
              />
              {errors.data_ocorrencia && <span className="error-message">{errors.data_ocorrencia}</span>}
            </div>

            <div className="form-group">
              <label className="form-label">Local</label>
              <input
                type="text"
                name="local"
                value={formData.local}
                onChange={handleInputChange}
                className="form-control"
                placeholder="Ex: Avenida Paulista, 1000"
              />
            </div>
          </div>
        </div>

        {/* Descrição */}
        <div style={{ marginBottom: '30px' }}>
          <h3 style={{ marginBottom: '20px', color: '#2c3e50', borderBottom: '2px solid #04C64F', paddingBottom: '10px' }}>
            Descrição da Ocorrência
          </h3>

          <div className="form-group">
            <label className="form-label">Descrição *</label>
            <textarea
              name="descricao"
              value={formData.descricao}
              onChange={handleInputChange}
              className={`form-control ${errors.descricao ? 'error' : ''}`}
              placeholder="Descreva detalhadamente o que aconteceu..."
              rows={6}
            />
            {errors.descricao && <span className="error-message">{errors.descricao}</span>}
          </div>
        </div>

        {/* Envolvidos */}
        <div style={{ marginBottom: '30px' }}>
          <h3 style={{ marginBottom: '20px', color: '#2c3e50', borderBottom: '2px solid #04C64F', paddingBottom: '10px' }}>
            Envolvidos
          </h3>

          <div className="info-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
            <div className="form-group">
              <label className="form-label">Veículo</label>
              <select
                name="veiculo_id"
                value={formData.veiculo_id}
                onChange={handleInputChange}
                className="form-control"
              >
                <option value="">Selecione um veículo</option>
                {vehicles.map(vehicle => (
                  <option key={vehicle.id} value={vehicle.id}>
                    {vehicle.placa} - {vehicle.marca} {vehicle.modelo}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Motorista</label>
              <select
                name="motorista_id"
                value={formData.motorista_id}
                onChange={handleInputChange}
                className="form-control"
              >
                <option value="">Selecione um motorista</option>
                {drivers.map(driver => (
                  <option key={driver.id} value={driver.id}>
                    {driver.nome}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Responsável pela Resolução</label>
              <input
                type="text"
                name="responsavel"
                value={formData.responsavel}
                onChange={handleInputChange}
                className="form-control"
                placeholder="Nome do responsável"
              />
            </div>
          </div>
        </div>

        {/* Resolução (apenas se status for resolvida) */}
        {formData.status === 'Resolvida' && (
          <div style={{ marginBottom: '30px' }}>
            <h3 style={{ marginBottom: '20px', color: '#2c3e50', borderBottom: '2px solid #04C64F', paddingBottom: '10px' }}>
              Resolução
            </h3>

            <div className="info-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', marginBottom: '20px' }}>
              <div className="form-group">
                <label className="form-label">Data e Hora da Resolução</label>
                <input
                  type="datetime-local"
                  name="data_resolucao"
                  value={formData.data_resolucao}
                  onChange={handleInputChange}
                  className="form-control"
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Observações da Resolução</label>
              <textarea
                name="observacoes_resolucao"
                value={formData.observacoes_resolucao}
                onChange={handleInputChange}
                className="form-control"
                placeholder="Descreva como a ocorrência foi resolvida..."
                rows={4}
              />
            </div>
          </div>
        )}

        {/* Ações do Formulário */}
        <div className="form-actions">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={handleCancel}
            disabled={isLoading}
          >
            Cancelar
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <span className="loading"></span>
                Cadastrando...
              </>
            ) : (
              <>
                <i className="fas fa-save"></i>
                Cadastrar Ocorrência
              </>
            )}
          </button>
        </div>
      </form>
    </FormLayout>
  );
};

export default OccurrenceCreate;
